import { useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  CalendarIcon,
  CloseIcon,
  HomeIcon,
  SettingsIcon,
  UserIcon,
} from "../../components/Icons";
import { useAuth } from "../../contexts/AuthContext";

interface EmployeeInterviewLayoutProps {
  requiredRole?: "admin" | "interviewer";
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  title: string;
  // admin 専用なら true、誰でも見えるなら false。is_admin 専用フラグだけで管理する
  adminOnly: boolean;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    title: "",
    adminOnly: false,
    items: [
      {
        name: "ホーム",
        href: "/employee_interview/dashboard",
        icon: HomeIcon,
      },
    ],
  },
  {
    title: "管理者",
    adminOnly: true,
    items: [
      {
        name: "スポンサー一覧",
        href: "/employee_interview/sponsors",
        icon: HomeIcon,
      },
      {
        name: "ユーザー一覧",
        href: "/employee_interview/users",
        icon: UserIcon,
      },
      {
        name: "インタビュー一覧",
        href: "/employee_interview/interviews",
        icon: CalendarIcon,
      },
      {
        name: "テンプレートスクリプト",
        href: "/employee_interview/template-presets",
        icon: SettingsIcon,
      },
    ],
  },
  {
    title: "インタビュアー",
    adminOnly: false,
    items: [
      {
        name: "担当インタビュー",
        href: "/employee_interview/interviewer/dashboard",
        icon: CalendarIcon,
      },
      {
        name: "カレンダー連携",
        href: "/employee_interview/google-connect",
        icon: SettingsIcon,
      },
    ],
  },
];

export function EmployeeInterviewLayout({
  requiredRole,
}: EmployeeInterviewLayoutProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (requiredRole === "admin" && user && !user.isAdmin) {
    return (
      <Navigate
        to="/employee_interview/login"
        state={{ from: location }}
        replace
      />
    );
  }

  const filteredSections = navigationSections.filter(
    (section) => !section.adminOnly || user?.isAdmin,
  );
  const roleLabel = user?.isAdmin ? "admin" : "interviewer";

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <div className="flex min-h-screen bg-[var(--color-bg-subtle-gray)]">
      {/* サイドバー（デスクトップ） */}
      <aside className="fixed inset-y-0 hidden w-56 flex-col border-[var(--color-border-default)] border-r bg-white lg:flex">
        <div className="flex grow flex-col overflow-y-auto pt-5 pb-4">
          <div className="mb-6 flex shrink-0 items-center px-4">
            <h1 className="font-bold text-[var(--color-primary-500)] text-lg">
              Interview Manager
            </h1>
          </div>
          <nav className="flex-1 px-2">
            {filteredSections.map((section, sectionIndex) => (
              <div
                key={section.title || `section-${sectionIndex}`}
                className="mb-4"
              >
                {sectionIndex > 0 && (
                  <div className="my-3 h-px bg-[var(--color-border-default)]" />
                )}
                {section.title && (
                  <p className="mb-2 px-3 font-semibold text-[11px] text-[var(--color-text-tertiary)] uppercase tracking-wide">
                    {section.title}
                  </p>
                )}
                <div>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive =
                      location.pathname === item.href ||
                      location.pathname.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`mb-0.5 flex items-center gap-2.5 rounded-lg px-3 py-2.5 font-medium text-sm transition-all ${
                          isActive
                            ? "bg-[var(--color-primary-500)] text-white"
                            : "text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-50)] hover:text-[var(--color-primary-500)]"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
          <div className="mt-auto px-2">
            <div className="my-3 h-px bg-[var(--color-border-default)]" />
            <div className="mb-2 flex items-center gap-2 p-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-primary-500)] font-medium text-[11px] text-white">
                {getInitials(user?.email || "")}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-[11px] text-[var(--color-text-primary)]">
                  {user?.email}
                </p>
                <div className="mt-0.5 flex gap-1">
                  <span className="rounded border border-[var(--color-primary-100)] bg-[var(--color-primary-50)] px-1.5 py-0.5 text-[9px] text-[var(--color-primary-500)]">
                    {roleLabel}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={signOut}
              className="flex w-full items-center gap-2 rounded-lg border-none bg-transparent px-3 py-2 text-left font-medium text-[var(--color-text-secondary)] text-xs hover:bg-[var(--color-bg-subtle-gray)]"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              ログアウト
            </button>
          </div>
        </div>
      </aside>

      {/* メインコンテンツ */}
      <div className="flex-1 lg:pl-56">
        {/* ヘッダー（モバイル） */}
        <header className="sticky top-0 z-50 border-[var(--color-border-default)] border-b bg-white lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--color-primary-500)]">
                <CalendarIcon className="h-4 w-4 text-white" />
              </div>
              <h1 className="font-bold text-[var(--color-primary-500)] text-lg">
                Interview Manager
              </h1>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="rounded-lg border-none bg-transparent p-2 text-[var(--color-text-secondary)]"
            >
              {sidebarOpen ? (
                <CloseIcon className="h-5 w-5" />
              ) : (
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </header>

        {/* モバイルサイドバー */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <aside
              className="h-full w-64 border-[var(--color-border-default)] border-r bg-white shadow-[2px_0_8px_rgba(0,0,0,0.1)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-full flex-col pt-6 pb-4">
                <div className="mb-8 flex items-center justify-between px-6">
                  <h1 className="font-bold text-[var(--color-primary-500)] text-xl">
                    Interview Manager
                  </h1>
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="rounded-lg border-none bg-transparent p-1.5 text-[var(--color-text-secondary)]"
                  >
                    <CloseIcon className="h-5 w-5" />
                  </button>
                </div>
                <nav className="flex-1 overflow-y-auto px-4">
                  {filteredSections.map((section, sectionIndex) => (
                    <div key={section.title} className="mb-4">
                      {sectionIndex > 0 && (
                        <div className="my-3 h-px bg-[var(--color-border-default)]" />
                      )}
                      <p className="mb-2 px-3 font-semibold text-[11px] text-[var(--color-text-tertiary)] uppercase tracking-wide">
                        {section.title}
                      </p>
                      <div>
                        {section.items.map((item) => {
                          const Icon = item.icon;
                          const isActive =
                            location.pathname === item.href ||
                            location.pathname.startsWith(item.href);
                          return (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setSidebarOpen(false)}
                              className={`mb-1 flex items-center gap-3 rounded-lg p-3 font-medium text-sm transition-all ${
                                isActive
                                  ? "bg-[var(--color-primary-500)] text-white"
                                  : "text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-50)] hover:text-[var(--color-primary-500)]"
                              }`}
                            >
                              <Icon className="h-5 w-5" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </nav>
                <div className="mt-auto px-4">
                  <div className="my-4 h-px bg-[var(--color-border-default)]" />
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-primary-500)] text-white text-xs">
                      {getInitials(user?.email || "")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-[13px] text-[var(--color-text-primary)]">
                        {user?.email}
                      </p>
                      <div className="mt-1 flex gap-1">
                        <span className="rounded border border-[var(--color-primary-100)] bg-[var(--color-primary-50)] px-2 py-0.5 text-[10px] text-[var(--color-primary-500)]">
                          {roleLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={signOut}
                    className="mt-2 flex w-full items-center gap-3 rounded-lg border-none bg-transparent p-3 text-left text-[var(--color-text-secondary)] text-sm hover:bg-[var(--color-bg-subtle-gray)]"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    ログアウト
                  </button>
                </div>
              </div>
            </aside>
          </div>
        )}

        {/* メインコンテンツエリア */}
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
